import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { ArrowLeft, Save, Package, Loader2 } from "lucide-react";

type Mode = "create" | "edit";

type Peca = {
  id: string;
  codigo?: string | null;
  descricao: string;
  marca?: string | null;
  unidade?: string | null;
  ncm?: string | null;
  custo?: any;
  precoVenda?: any;
  estoqueAtual?: any;
  estoqueMin?: any;
};


const UNIDADES = [
  { value: "un", label: "Unidade (un)" },
  { value: "pc", label: "Peça (pc)" },
  { value: "jg", label: "Jogo (jg)" },
  { value: "kit", label: "Kit (kit)" },
  { value: "cx", label: "Caixa (cx)" },
  { value: "lt", label: "Litro (lt)" },
  { value: "ml", label: "Mililitro (ml)" },
  { value: "kg", label: "Quilo (kg)" },
  { value: "g", label: "Grama (g)" },
  { value: "m", label: "Metro (m)" },
]


function toStr(v: any) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function onlyNumberDotComma(s: string) {
  return s.replace(/[^\d.,-]/g, "");
}

function money(v: any) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}


function normalizeDecimalInput(s: string) {
  // aceita 12,34 ou 12.34 e manda como "12.34"
  const cleaned = onlyNumberDotComma(s).trim();
  if (!cleaned) return "";
  // se tiver vírgula e ponto, assume formato BR "1.234,56"
  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");
  if (hasComma && hasDot) {
    // remove separador de milhar
    const noThousands = cleaned.replace(/\./g, "").replace(",", ".");
    return noThousands;
  }
  // se só vírgula: troca por ponto
  if (hasComma && !hasDot) return cleaned.replace(",", ".");
  return cleaned;
}

function isValidDecimal(s: string) {
  if (!s) return true; // vazio permitido
  return /^-?\d+(\.\d+)?$/.test(s);
}


function formatBRLFromNumericString(value: string) {
  const normalized = normalizeDecimalInput(value);
  const n = Number(normalized);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function parseBRLToNumericString(value: string) {
  // "R$ 1.234,56" -> "1234.56"
  const cleaned = value.replace(/\s/g, "").replace("R$", "");
  return normalizeDecimalInput(cleaned);
}



export default function PecaForm({ mode }: { mode: Mode }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEdit = mode === "edit";
  const title = isEdit ? "Editar peça" : "Nova peça";

  const [loading, setLoading] = useState<boolean>(isEdit);
  const [saving, setSaving] = useState<boolean>(false);

  const [codigo, setCodigo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [marca, setMarca] = useState("");
  const [unidade, setUnidade] = useState("un");
  const [ncm, setNcm] = useState("");

  const [custo, setCusto] = useState("");
  const [precoVenda, setPrecoVenda] = useState("");
  const [estoqueMin, setEstoqueMin] = useState("");

  const disabledSave = useMemo(() => {
    if (saving) return true;
    if (!descricao.trim() || descricao.trim().length < 2) return true;

    const custoN = parseBRLToNumericString(custo);
    const vendaN = parseBRLToNumericString(precoVenda);

    const minN = normalizeDecimalInput(estoqueMin);

    if (!isValidDecimal(custoN) || !isValidDecimal(vendaN) || !isValidDecimal(minN)) return true;
    return false;
  }, [descricao, custo, precoVenda, estoqueMin, saving]);

  async function load() {
    if (!isEdit || !id) return;

    setLoading(true);
    try {
      const { data } = await api.get(`/api/pecas/${id}`);
      const p: Peca = data?.peca;

      setCodigo(toStr(p.codigo));
      setDescricao(toStr(p.descricao));
      setMarca(toStr(p.marca));
      setUnidade(toStr(p.unidade) || "un");
      setNcm(toStr(p.ncm));

      setCusto(toStr(p.custo ?? ""));
      setPrecoVenda(toStr(p.precoVenda ?? ""));
      setEstoqueMin(toStr(p.estoqueMin ?? ""));
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || "Erro ao carregar peça";
      toast.error(msg);
      navigate("/pecas");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (disabledSave) return;

    const payload: any = {
      codigo: codigo.trim() ? codigo.trim() : null,
      descricao: descricao.trim(),
      marca: marca.trim() ? marca.trim() : null,
      unidade: unidade.trim() ? unidade.trim() : "un",
      ncm: ncm.trim() ? ncm.trim() : null,
    };

    const custoN = parseBRLToNumericString(custo);
    const vendaN = parseBRLToNumericString(precoVenda);

    const minN = normalizeDecimalInput(estoqueMin);

    if (custoN) payload.custo = custoN;
    if (vendaN) payload.precoVenda = vendaN;
    if (minN) payload.estoqueMin = minN;

    setSaving(true);
    try {
      if (isEdit && id) {
        await api.patch(`/api/pecas/${id}`, payload);
        toast.success("Peça atualizada com sucesso ✅");
      } else {
        await api.post("/api/pecas", payload);
        toast.success("Peça criada com sucesso ✅");
      }
      navigate("/pecas");
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || "Erro ao salvar peça";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Topo */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Package size={20} />
              {title}
            </h1>
            <p className="text-sm text-slate-500">
              {isEdit ? "Atualize os dados da peça" : "Cadastre uma peça para o catálogo"}
            </p>
          </div>

          <Link to="/pecas">
            <Button variant="outline" className="gap-2">
              <ArrowLeft size={16} />
              Voltar
            </Button>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          {loading ? (
            <div className="flex items-center justify-center py-14 text-slate-500 gap-2">
              <Loader2 className="animate-spin" size={18} />
              Carregando...
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-6">
              {/* Linha 1 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Código (opcional)</Label>
                  <Input
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                    placeholder="Ex: P-0001 / 123456"
                  />
                  <p className="text-xs text-slate-500">Aceita texto ou número.</p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">
                    Descrição *
                  </label>

                  <Input
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    placeholder="Ex: Óleo 5W30 Sintético"
                    required
                  />
                </div>
              </div>

              {/* Linha 2 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Marca (opcional)</Label>
                  <Input value={marca} onChange={(e) => setMarca(e.target.value)} placeholder="Ex: Mobil, Bosch..." />
                </div>

                <div className="space-y-2">
                  <Label>Unidade</Label>
                  <select
                    value={unidade}
                    onChange={(e) => setUnidade(e.target.value)}
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  >
                    {UNIDADES.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </div>


                <div className="space-y-2">
                  <Label>NCM (opcional)</Label>
                  <Input value={ncm} onChange={(e) => setNcm(e.target.value)} placeholder="Ex: 27101932" />
                </div>
              </div>

              {/* Linha 3 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Custo (opcional)</Label>
                  <Input
                    value={custo}
                    onChange={(e) => {
                      // deixa digitar livre
                      setCusto(e.target.value);
                    }}
                    onFocus={() => {
                      // tira R$ pra facilitar editar
                      const raw = parseBRLToNumericString(custo);
                      setCusto(raw ? raw.replace(".", ",") : "");
                    }}
                    onBlur={() => {
                      const raw = parseBRLToNumericString(custo);
                      setCusto(raw ? formatBRLFromNumericString(raw) : "");
                    }}
                    placeholder="R$ 0,00"
                    inputMode="decimal"
                  />
                  <p className="text-xs text-slate-500">Pode digitar 10,50 ou 10.50</p>
                </div>

                <div className="space-y-2">
                  <Label>Preço de venda (opcional)</Label>
                  <Input
                    value={precoVenda}
                    onChange={(e) => setPrecoVenda(e.target.value)}
                    onFocus={() => {
                      const raw = parseBRLToNumericString(precoVenda);
                      setPrecoVenda(raw ? raw.replace(".", ",") : "");
                    }}
                    onBlur={() => {
                      const raw = parseBRLToNumericString(precoVenda);
                      setPrecoVenda(raw ? formatBRLFromNumericString(raw) : "");
                    }}
                    placeholder="R$ 0,00"
                    inputMode="decimal"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Estoque mínimo (opcional)</Label>
                  <Input
                    value={estoqueMin}
                    onChange={(e) => setEstoqueMin(e.target.value)}
                    placeholder="0"
                    inputMode="decimal"
                  />
                </div>
              </div>

              {/* Informativo */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <b>Estoque atual</b> não é editado aqui.
                <br />
                Para manter histórico, o ajuste entra por <b>Movimentar Estoque</b>.
              </div>

              {/* Ações */}
              <div className="flex items-center justify-end gap-2">
                <Link to="/pecas">
                  <Button type="button" variant="outline" disabled={saving}>
                    Cancelar
                  </Button>
                </Link>

                <Button type="submit" disabled={disabledSave} className="gap-2">
                  <Save size={16} />
                  {saving ? "Salvando..." : "Salvar"}
                </Button>
              </div>

              {/* Preview útil */}
              <div className="text-xs text-slate-500">
                Custo: <span className="font-medium text-slate-700">{custo ? money(normalizeDecimalInput(custo)) : "-"}</span>{" "}
                • Venda:{" "}
                <span className="font-medium text-slate-700">
                  {precoVenda ? money(normalizeDecimalInput(precoVenda)) : "-"}
                </span>
              </div>
            </form>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
